import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import endpoints from "../../../api/endpoints";
import api from "../../../api/axios";
import { extractApiErrorMessage } from "../../../utils/apiError";

// Initial state
interface CannedResponse {
  crId: number;
  crName: string;
  crSubject: string;
  crBody: string;
}

interface CannedResponseDropdownOption {
  crId: number;
  crName: string;
}

interface ManageCannedResponseState {
  cannedResponses: CannedResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: ManageCannedResponseState = {
  cannedResponses: [],
  loading: false,
  error: null,
};

// Async thunk to fetch canned responses
export const fetchCannedResponses = createAsyncThunk<
  CannedResponse[],
  void,
  { rejectValue: string }
>("tuition/fetchCannedResponses", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(
      endpoints.tuition.admin.manageCannedResponse.getCannedResponses
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(extractApiErrorMessage(error, "An error occurred while fetching canned responses"));
  }
});

// Async thunk to fetch a specific canned response by ID
export const fetchCannedResponseById = createAsyncThunk<
  CannedResponse,
  number,
  { rejectValue: string }
>("tuition/fetchCannedResponseById", async (id, { rejectWithValue }) => {
  try {
    const response = await api.get(
      endpoints.tuition.admin.manageCannedResponse.getCannedResponseById(id)
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(
        error,
        `An error occurred while fetching canned response with ID ${id}`,
      ),
    );
  }
});

// Async thunk to update a canned response
export const updateCannedResponse = createAsyncThunk<
  CannedResponse,
  { id: number; data: Partial<CannedResponse> },
  { rejectValue: string }
>("tuition/updateCannedResponse", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.patch(
      endpoints.tuition.admin.manageCannedResponse.updateCannedResponse(id),
      data
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(
        error,
        `An error occurred while updating canned response with ID ${id}`,
      ),
    );
  }
});

// Async thunk to create a new canned response
export const createCannedResponse = createAsyncThunk<
  CannedResponse,
  { crName: string; crSubject: string; crBody: string },
  { rejectValue: string }
>("tuition/createCannedResponse", async (data, { rejectWithValue }) => {
  try {
    const response = await api.post(
      endpoints.tuition.admin.manageCannedResponse.createCannedResponse,
      data
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(
        error,
        "An error occurred while creating canned response",
      ),
    );
  }
});

// Async thunk to delete a canned response
export const deleteCannedResponse = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("tuition/deleteCannedResponse", async (id, { rejectWithValue }) => {
  try {
    await api.delete(
      endpoints.tuition.admin.manageCannedResponse.deleteCannedResponse(id)
    );
    return id;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(
        error,
        `An error occurred while deleting canned response with ID ${id}`,
      ),
    );
  }
});

// Selector function to transform canned responses for dropdown
export const selectCannedResponseOptions = (
  state: { tuitionManageCannedResponse: ManageCannedResponseState }
): CannedResponseDropdownOption[] => {
  return state.tuitionManageCannedResponse.cannedResponses.map((response) => ({
    crId: response.crId,
    crName: response.crName,
  }));
};

// Slice
const ManageCannedResponseSlice = createSlice({
  name: "ManageCannedResponse",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearCannedResponses(state) {
      state.cannedResponses = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCannedResponses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCannedResponses.fulfilled,
        (state, action: PayloadAction<CannedResponse[]>) => {
          state.loading = false;
          state.cannedResponses = action.payload;
        }
      )
      .addCase(
        fetchCannedResponses.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : "Failed to fetch canned responses";
        }
      )
      .addCase(fetchCannedResponseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCannedResponseById.fulfilled,
        (state, action: PayloadAction<CannedResponse>) => {
          state.loading = false;
          // You can either update the cannedResponses array or add a separate field for single response
          // For now, I'll add/update it in the array
          const index = state.cannedResponses.findIndex(
            (response) => response.crId === action.payload.crId
          );
          if (index === -1) {
            state.cannedResponses.push(action.payload);
          } else {
            state.cannedResponses[index] = action.payload;
          }
        }
      )
      .addCase(
        fetchCannedResponseById.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : "Failed to fetch canned response";
        }
      )
      .addCase(updateCannedResponse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateCannedResponse.fulfilled,
        (state, action: PayloadAction<CannedResponse>) => {
          state.loading = false;
          const index = state.cannedResponses.findIndex(
            (response) => response.crId === action.payload.crId
          );
          if (index !== -1) {
            state.cannedResponses[index] = action.payload;
          }
        }
      )
      .addCase(
        updateCannedResponse.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : "Failed to update canned response";
        }
      )
      .addCase(createCannedResponse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createCannedResponse.fulfilled,
        (state, action: PayloadAction<CannedResponse>) => {
          state.loading = false;
          state.cannedResponses.push(action.payload);
        }
      )
      .addCase(
        createCannedResponse.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : "Failed to create canned response";
        }
      )
      .addCase(deleteCannedResponse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteCannedResponse.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.cannedResponses = state.cannedResponses.filter(
            (response) => response.crId !== action.payload
          );
        }
      )
      .addCase(
        deleteCannedResponse.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : "Failed to delete canned response";
        }
      );
  },
});

export const { clearError, clearCannedResponses } = ManageCannedResponseSlice.actions;

export default ManageCannedResponseSlice.reducer;
